import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams,Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import importImage from '../../utils/images/importImage';
import { showToast } from '../../utils/toast';
import { postApi } from '../../utils/api/api';
import { Eye, EyeOff } from 'lucide-react';

const ResetPasswordForm = () => {
  const LoginFormImage = importImage('login-form.jpg');
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const emailFromLink = searchParams.get('email') || '';

  const [formData, setFormData] = useState({
    email: emailFromLink,
    password: '',
    password_confirmation: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  useEffect(() => {
    if (!token || !emailFromLink) {
      showToast('error', t('errors.invalidResetLink', 'Invalid password reset link.'));
      navigate('/forgot-password', { replace: true });
    }
  }, [token, emailFromLink, navigate, t]);

  const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);
  const toggleConfirmPasswordVisibility = () => setConfirmPasswordVisible(!confirmPasswordVisible);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const { email, password, password_confirmation } = formData;
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email.trim()) {
      newErrors.email = t('errors.emailRequired', 'Email is required.');
    } else if (!emailRegex.test(email)) {
      newErrors.email = t('errors.invalidEmail', 'Please enter a valid email address.');
    }

    if (!password.trim()) {
      newErrors.password = t('errors.passwordRequired', 'Password is required.');
    } else if (password.length < 8) {
      newErrors.password = t('errors.passwordTooShort', 'Password must be at least 8 characters long.');
    }

    if (!password_confirmation.trim()) {
      newErrors.password_confirmation = t('errors.confirmPasswordRequired', 'Confirm password is required.');
    } else if (password !== password_confirmation) {
      newErrors.password_confirmation = t('errors.passwordMismatch', 'Passwords do not match.');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await postApi('/reset-password', {
        token,
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.password_confirmation
      });

      const message = response?.message || t('success.passwordReset', 'Password reset successfully!');
      showToast('success', message);
      navigate('/login', { replace: true });
    } catch (err) {
      const errorMessage = err.response?.data?.message || t('errors.resetPasswordFailed', 'Failed to reset password.');
      showToast('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page bg-light min-vh-100 d-flex align-items-center py-5">
      <div className="container">
        <div className="row g-0">
          <div className="col-12 col-lg-12">
            <div className="card shadow-lg border-0 overflow-hidden" style={{
              borderRadius: '20px',
              border: '1px solid rgba(0,0,0,0.1)',
              boxShadow: '0 15px 30px rgba(0,0,0,0.12)'
            }}>
              <div className="row g-0 h-100">

                {/* Form Column */}
                <div className="col-lg-5 d-flex align-items-center">
                  <div className="card-body p-4 p-md-5">
                    <div className="text-center mb-4">
                      <h2 className="text-primary fw-bold">
                        {t('resetPassword.heading', 'Reset Your Password')}
                      </h2>
                      {/*<p className="text-muted">
                        {t('resetPassword.subheading', 'Enter your new password below')}
                      </p>*/}
                    </div>

                    <form onSubmit={handleSubmit} noValidate>
                      
                      {/* Email */}
                      <div className="mb-4">
                        <label htmlFor="email" className="form-label fw-semibold">
                          {t('resetPassword.emailLabel', 'Email Address')}
                          <span className="text-danger">*</span>
                        </label>
                        <div className="input-group has-validation">
                          <span className="input-group-text bg-light border-end-0">
                            <i className="bi bi-envelope text-muted"></i>
                          </span>
                          <input
                            id="email"
                            type="email"
                            name="email"
                            className={`form-control py-3 ${errors.email ? 'is-invalid' : ''}`}
                            value={formData.email}
                            onChange={handleChange}
                            readOnly
                          />
                          {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                        </div>
                      </div>

                      {/* New Password */}
                      <div className="mb-4">
                        <label htmlFor="password" className="form-label fw-semibold">
                          {t('resetPassword.passwordLabel', 'New Password')}
                          <span className="text-danger">*</span>
                        </label>
                        <div className="input-group has-validation">
                          <span className="input-group-text bg-light border-end-0">
                            <i className="bi bi-lock text-muted"></i>
                          </span>
                          <input
                            id="password"
                            type={passwordVisible ? 'text' : 'password'}
                            name="password"
                            className={`form-control py-3 ${errors.password ? 'is-invalid' : ''}`}
                            placeholder={t('placeholders.newPassword', 'Enter your new password')}
                            value={formData.password}
                            onChange={handleChange}
                          />
                          <button
                            type="button"
                            className="input-group-text bg-light border-start-0"
                            onClick={togglePasswordVisibility}
                          >
                            {passwordVisible ? <EyeOff size={18} className="text-muted" /> : <Eye size={18} className="text-muted" />}
                          </button>
                          {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                        </div>
                      </div>

                      {/* Confirm Password */}
                      <div className="mb-4">
                        <label htmlFor="password_confirmation" className="form-label fw-semibold">
                          {t('resetPassword.confirmPasswordLabel', 'Confirm Password')}
                          <span className="text-danger">*</span>
                        </label>
                        <div className="input-group has-validation">
                          <span className="input-group-text bg-light border-end-0">
                            <i className="bi bi-lock-fill text-muted"></i>
                          </span>
                          <input
                            id="password_confirmation"
                            type={confirmPasswordVisible ? 'text' : 'password'}
                            name="password_confirmation"
                            className={`form-control py-3 ${errors.password_confirmation ? 'is-invalid' : ''}`}
                            placeholder={t('placeholders.confirmPassword', 'Confirm your new password')}
                            value={formData.password_confirmation}
                            onChange={handleChange}
                          />
                          <button
                            type="button"
                            className="input-group-text bg-light border-start-0"
                            onClick={toggleConfirmPasswordVisibility}
                          >
                            {confirmPasswordVisible ? <EyeOff size={18} className="text-muted" /> : <Eye size={18} className="text-muted" />}
                          </button>
                          {errors.password_confirmation && <div className="invalid-feedback">{errors.password_confirmation}</div>}
                        </div>
                      </div>

                      <div className="d-grid mb-3">
                        <button
                          type="submit"
                          className="btn btn-primary py-3 fw-semibold"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              {t('buttons.resetting', 'Resetting...')}
                            </>
                          ) : (
                            t('buttons.reset', 'Reset Password')
                          )}
                        </button>
                      </div>

                      <div className="text-center mt-4 pt-3 border-top">
                        <p className="mb-0">
                          <Link to="/login" className="text-primary fw-semibold text-decoration-none">
                            {t('resetPassword.backToLogin', 'Back to Login')}
                          </Link>
                        </p>
                      </div>
                    </form>
                  </div>
                </div>

                {/* Image Column */}
                <div className="col-lg-7 d-none d-lg-flex position-relative">
                  <div className="h-100 w-100" style={{
                    backgroundImage: `url(${LoginFormImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderLeft: '1px solid rgba(0,0,0,0.1)',
                    borderTopRightRadius: '20px',
                    borderBottomRightRadius: '20px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div className="position-absolute top-0 start-0 w-100 h-100" style={{
                      background: 'linear-gradient(135deg, rgba(13,110,253,0.15) 0%, rgba(255,255,255,0) 100%)'
                    }}></div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
